import { formatSaveDateFrom, formatSaveDateTo } from 'reducers/TimeReducer';
import { EVIDENCE_RECORDS_EMPTY_STATS } from '../constants/evidenceRecordsFilters';
import { getDaysInEvidence } from './daysInEvidence';

export const normalizeEvidenceRecordFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value : null;
  }

  return value || null;
};

export const buildEvidenceRecordsSearchQuery = ({
  filters = {},
  agencyId,
  sortField,
  sortDirection,
  page = 0,
  pageSize = 100,
}) => ({
  mode: 'search',
  page,
  pageSize,
  AgencyID: agencyId,
  QuickFilter: filters.searchText || null,
  SubmittedFrom: filters.submittedDate?.from
    ? formatSaveDateFrom(filters.submittedDate.from)
    : null,
  SubmittedTo: filters.submittedDate?.to ? formatSaveDateTo(filters.submittedDate.to) : null,
  RequiredActionFrom: filters.requiredActionDate?.from
    ? formatSaveDateFrom(filters.requiredActionDate.from)
    : null,
  RequiredActionTo: filters.requiredActionDate?.to
    ? formatSaveDateTo(filters.requiredActionDate.to)
    : null,
  Category: normalizeEvidenceRecordFilterValue(filters.category),
  PropertyType: normalizeEvidenceRecordFilterValue(filters.propertyType),
  EvidenceType: normalizeEvidenceRecordFilterValue(filters.evidenceType),
  TemporaryLocation: normalizeEvidenceRecordFilterValue(filters.tempLocation),
  CurrentLocation: normalizeEvidenceRecordFilterValue(filters.currentLocation),
  SubmittedBy: normalizeEvidenceRecordFilterValue(filters.submittedBy),
  InvolvedBy: normalizeEvidenceRecordFilterValue(filters.involvedBy),
  RequiredAction: normalizeEvidenceRecordFilterValue(filters.requiredAction),
  Status: normalizeEvidenceRecordFilterValue(filters.status),
  HasCorrection: filters.hasCorrection || null,
  CorrectionStatus: normalizeEvidenceRecordFilterValue(filters.correctionStatus),
  CorrectionReason: normalizeEvidenceRecordFilterValue(filters.correctionReason),
  CorrectionRoutedTo: normalizeEvidenceRecordFilterValue(filters.correctionRoutedTo),
  Overdue: filters.overdue || null,
  EvidenceID: normalizeEvidenceRecordFilterValue(filters.EvidenceID),
  IncidentNumber: normalizeEvidenceRecordFilterValue(filters.IncidentID),
  TagNumber: normalizeEvidenceRecordFilterValue(filters.TagNumber),
  CollectedBy: normalizeEvidenceRecordFilterValue(filters.CollectedBy),
  LocationCollected: normalizeEvidenceRecordFilterValue(filters.LocationCollected),
  Make: normalizeEvidenceRecordFilterValue(filters.Make),
  Model: normalizeEvidenceRecordFilterValue(filters.Model),
  SerialNumber: normalizeEvidenceRecordFilterValue(filters.SerialNumber),
  VIN: normalizeEvidenceRecordFilterValue(filters.VIN),
  LicenseNumber: normalizeEvidenceRecordFilterValue(filters.LicenseNumber),
  SubstanceType: normalizeEvidenceRecordFilterValue(filters.SubstanceType),
  SubstanceClass: normalizeEvidenceRecordFilterValue(filters.SubstanceClass),
  StorageLocation: normalizeEvidenceRecordFilterValue(filters.StorageLocation),
  SubLocation: normalizeEvidenceRecordFilterValue(filters.SubLocation),
  Container: normalizeEvidenceRecordFilterValue(filters.Container),
  DisposalCode: normalizeEvidenceRecordFilterValue(filters.disposalCode),
  Offense: normalizeEvidenceRecordFilterValue(filters.offense),
  CollectedFrom: filters.collectedDate?.from
    ? formatSaveDateFrom(filters.collectedDate.from)
    : null,
  CollectedTo: filters.collectedDate?.to ? formatSaveDateTo(filters.collectedDate.to) : null,
  FinalDisposition: normalizeEvidenceRecordFilterValue(filters.FinalDisposition),
  DisposalFrom: filters.dispositionDate?.from
    ? formatSaveDateFrom(filters.dispositionDate.from)
    : null,
  DisposalTo: filters.dispositionDate?.to ? formatSaveDateTo(filters.dispositionDate.to) : null,
  PartyType: normalizeEvidenceRecordFilterValue(filters.PartyType),
  PartyName: normalizeEvidenceRecordFilterValue(filters.PartyName),
  ClearanceLevel: filters.ClearanceLevel ?? null,
  SortField: sortField || null,
  SortDirection: sortDirection ? sortDirection.toUpperCase() : null,
});

export const parseEvidenceRecordResults = (result = []) =>
  result.map((item) => {
    const parsedItem = { ...item, id: item.ptsEvidenceID };

    ['EvidenceCustody', 'EvidenceStorage', 'EvidenceDisposition', 'Offenses'].forEach((key) => {
      if (typeof parsedItem[key] === 'string') {
        try {
          parsedItem[key] = JSON.parse(parsedItem[key]);
        } catch (error) {
          console.error('Error in parse Evidence Records', error);
        }
      }
    });

    parsedItem.DaysInEvidence = getDaysInEvidence({
      receivedDate: item.ReceivedDate,
      submittedAt: item.SubmittedAt,
      dateTimeCollected: item.DateTimeCollected,
      isDisposed: item.IsDisposed,
      dispositionDate: item.DispositionDate,
    });

    return parsedItem;
  });

export const mapDisposalRelevantItems = (items = []) =>
  (items || []).map((item) => ({
    ...item,
    DaysInEvidence: getDaysInEvidence({
      receivedDate: item.ReceivedDate,
      submittedAt: item.SubmittedAt,
      dateTimeCollected: item.DateTimeCollected,
      isDisposed: item.IsDisposed,
      dispositionDate: item.DispositionDate,
    }),
  }));

export const mapEvidenceRecordsSearchResponse = (response) => ({
  items: parseEvidenceRecordResults(response?.result || []),
  total: response?.rowCount || 0,
  stats: response?.stats || EVIDENCE_RECORDS_EMPTY_STATS,
  raw: response,
});

export const uniq = (items, field) => {
  return [...new Set((items || []).map((i) => i[field]).filter(Boolean))].sort();
};

export function filterByEntity(codeList = [], entity) {
  if (!Array.isArray(codeList) || !entity) return [];

  return codeList.filter((item) => {
    if (!item?.Entities) return false;

    try {
      const entities = JSON.parse(item.Entities);
      return Array.isArray(entities) && entities.includes(entity);
    } catch {
      return false;
    }
  });
}
